import * as IF from "infrontjs";

/**
 * Anonymous usage statistics for the host (core-api GuestAppStatsService).
 *
 * Counts which sections of the app are opened and a handful of actions — no
 * names, no account, nothing stored on the device. The server tells visitors
 * apart by a key it derives per day from the request itself and does not keep
 * the IP, so there is no identifier here to store or to send.
 *
 * Events are queued in memory and sent in batches with navigator.sendBeacon:
 * when the app goes to the background (the last moment a phone reliably gives
 * us), when the queue fills up, or every 30 seconds. sendBeacon posts
 * text/plain, which needs no CORS preflight and survives the page closing.
 *
 * Off when there is no tenant (demo content) and when the browser signals
 * Do Not Track / Global Privacy Control.
 */
export class Tracker
{
    static FLUSH_INTERVAL_MS = 30000;
    static FLUSH_AT = 20;
    static MAX_QUEUE = 50;

    // State id → section the host sees. Detail pages count towards their list;
    // auth and gate screens are not usage of the house's content.
    static AREAS = {
        "home"            : "home",
        "info"            : "info",
        "recommendations" : "recommendations",
        "region"          : "region",
        "region-detail"   : "region",
        "place-detail"    : "region",
        "events"          : "events",
        "event-detail"    : "events",
        "plan"            : "plan",
        "chat"            : "chat",
        "survey"          : "feedback",
        "feedback"        : "feedback",
        "spa"             : "spa"
    };

    constructor( app )
    {
        this.app = app;
        this._queue = [];
        this._timer = null;
        this._started = false;
    }

    /** Called once the tenant is resolved (App.init). */
    start()
    {
        if ( this._started || !this._isEnabled() )
        {
            return;
        }
        this._started = true;

        this.app.addEventListener( IF.CustomEvents.TYPE.AFTER_STATE_CHANGE, ( e ) =>
        {
            const area = Tracker.AREAS[ e?.detail?.currentStateId ];
            if ( area )
            {
                this.track( "view", area );
            }
        } );

        window.addEventListener( "appinstalled", () => this.track( "install" ) );

        const flushNow = () => this.flush();
        document.addEventListener( "visibilitychange", () =>
        {
            if ( document.visibilityState === "hidden" )
            {
                flushNow();
            }
        } );
        window.addEventListener( "pagehide", flushNow );
        window.addEventListener( "online", flushNow );

        this._timer = window.setInterval( flushNow, Tracker.FLUSH_INTERVAL_MS );
    }

    /**
     * @param {string} type view | install | push_optin | chat_sent | plan_add | survey_done
     * @param {string|null} [area] for views: the section (see AREAS)
     */
    track( type, area = null )
    {
        if ( !this._started )
        {
            return;
        }

        this._queue.push( { "type" : type, "area" : area, "at" : Date.now() } );
        if ( this._queue.length > Tracker.MAX_QUEUE )
        {
            this._queue.splice( 0, this._queue.length - Tracker.MAX_QUEUE );
        }
        if ( this._queue.length >= Tracker.FLUSH_AT )
        {
            this.flush();
        }
    }

    flush()
    {
        if ( !this._queue.length || !this.app.accommodation )
        {
            return;
        }
        // Offline: keep the queue (capped) for the next "online".
        if ( navigator.onLine === false )
        {
            return;
        }

        const body = JSON.stringify( {
            "lang"       : this.app.l18n.getCurrentLanguage(),
            "standalone" : this._isStandalone(),
            "events"     : this._queue
        } );
        const url = this.app.config.get( "api" ) + "/stats/collect/" + encodeURIComponent( this.app.accommodation.id );

        let sent = false;
        try
        {
            sent = typeof navigator.sendBeacon === "function"
                && navigator.sendBeacon( url, new Blob( [ body ], { type : "text/plain" } ) );
            if ( !sent )
            {
                fetch( url, { method : "POST", body, keepalive : true, headers : { "Content-Type" : "text/plain" } } )
                    .catch( () => {} );
                sent = true;
            }
        }
        catch ( e )
        {
            // Statistics never break the app.
        }

        if ( sent )
        {
            this._queue = [];
        }
    }

    _isEnabled()
    {
        if ( !this.app.accommodation || !this.app.accommodation.id )
        {
            return false;
        }
        // The host looking at their draft is not a visitor.
        if ( this.app.accommodation.isPreview )
        {
            return false;
        }
        if ( navigator.globalPrivacyControl === true || navigator.doNotTrack === "1" || window.doNotTrack === "1" )
        {
            return false;
        }
        return true;
    }

    _isStandalone()
    {
        return !!( ( window.matchMedia && window.matchMedia( "(display-mode: standalone)" ).matches )
            || window.navigator.standalone === true );
    }
}
